import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Chip,
  Button,
  Skeleton,
  Avatar,
  Divider,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { api } from "../../utils/api";
import { useAuth } from "../../context/AuthContext";

const statusColor = (s) => {
  switch ((s || "").toLowerCase()) {
    case "pending":
      return "warning";
    case "submitted":
      return "info";
    case "completed":
      return "success";
    case "under audit":
      return "primary";
    default:
      return "default";
  }
};

export default function Tasks() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/tasks/list.php");
        setTasks(res.tasks || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Avatar sx={{ bgcolor: "info.main", width: 48, height: 48 }}>
          <AssignmentIcon />
        </Avatar>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            Tasks
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your assigned tasks and their current status
          </Typography>
        </Box>
      </Stack>

      {loading && (
        <Stack spacing={2}>
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} variant="rounded" height={90} />
          ))}
        </Stack>
      )}

      {!loading && tasks.length === 0 && (
        <Paper sx={{ p: 6, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            No tasks available.
          </Typography>
        </Paper>
      )}

      <Stack spacing={2}>
        {!loading &&
          tasks.map((t) => (
            <Paper key={t.id} sx={{ p: 2.5 }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ md: "center" }}
                spacing={2}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {t.task_name}{" "}
                    {t.service_name && (
                      <Typography component="span" variant="body2" color="text.secondary">
                        · {t.service_name}
                      </Typography>
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Client: <b>{t.client_name || "—"}</b>
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    Due: {t.due_date || "—"}
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    size="small"
                    label={t.status || "Pending"}
                    color={statusColor(t.status)}
                  />
                  {user?.role === "Team" && t.status === "Pending" && (
                    <Button size="small" variant="contained">
                      Submit
                    </Button>
                  )}
                  {user?.role === "Manager" && t.status === "Submitted" && (
                    <Button size="small" variant="contained" color="success">
                      Approve
                    </Button>
                  )}
                  {user?.role === "Auditor" && t.status === "Under Audit" && (
                    <Button size="small" variant="contained" color="info">
                      Audit
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Paper>
          ))}
      </Stack>
    </Box>
  );
}
